import { Button, Input } from '@popsure/dirty-swan';
import { NavButton } from 'components/NavButton';
import { ChangeEvent, FormEvent } from 'react';
import { useSafeTranslation } from 'shared/i18n';

import { getErrorMessageTranslation } from '../../utils/getApiErrors';
import styles from './CodeConfirmation.module.scss';

type CodeConfirmationViewProps = {
  email: string | undefined;
  handleSubmit: (event: FormEvent) => void;
  handleOnChange: (event: ChangeEvent<HTMLInputElement>) => void;
  isLoading: boolean;
  code: string;
  error: unknown | null;
  onSendNewCode: () => void;
};

export const CodeConfirmationView = ({
  email,
  handleSubmit,
  handleOnChange,
  isLoading,
  code,
  error,
  onSendNewCode,
}: CodeConfirmationViewProps) => {
  const { t } = useSafeTranslation();

  return (
    <div className={styles.container}>
      <div className="p-body">
        <form onSubmit={handleSubmit}>
          <NavButton
            title={t('updateEmail.backButton', 'Back')}
            renderInPortal
          />
          <h1 className="p-h1">
            {t('updateEmail.codeConfirmation.title', "Let's verify it's you")}
          </h1>
          <div className="p-p mt8">
            {t(
              'updateEmail.codeConfirmation.subtitle',
              "We've sent a verification code to"
            )}{' '}
            <span className="fw-bold">{email}</span>
          </div>
          <button
            className="ds-interactive-component p-a fw-bold mt8"
            onClick={onSendNewCode}
            type="button"
          >
            {t('updateEmail.codeConfirmation.sendNewCode', 'Send a new code')}
          </button>
          <Input
            className={`wmn3 mt16 ${styles.input}`}
            value={code}
            type="text"
            data-testid="email-update-code-input"
            placeholder={t(
              'updateEmail.codeConfirmation.input.placeholder',
              'Temporary verification code'
            )}
            onChange={handleOnChange}
            required
            data-cy="update-email-login-code-input"
          />
          {error && (
            <div className="p-notice p-notice--danger mt16 wmx5">
              {getErrorMessageTranslation(t, error)}
            </div>
          )}
          <Button
            className="wmn3 mt16"
            onClick={handleSubmit}
            disabled={isLoading || !code}
            loading={isLoading}
          >
            {t('updateEmail.codeConfirmation.button.text', 'Continue')}
          </Button>
        </form>
      </div>
    </div>
  );
};
